import { ITextField } from '@fluentui/react/lib/TextField';
import { useTheme } from '@fluentui/react/lib/Theme';
import { IPTextField } from 'baseComponents/TextField/IPTextField';
import {
  CSSProperties,
  FocusEvent,
  FocusEventHandler,
  FormEvent,
  KeyboardEvent,
  KeyboardEventHandler,
  MouseEvent,
  MouseEventHandler,
  Ref,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

// A Fluent TextField specialization for code-like multiline values. Fluent owns the editable
// textarea, focus treatment, error state, disabled state and resize behavior. Tokens are marked
// with background highlights painted by a non-interactive mirror in TextField's public prefix
// slot; the glyphs themselves are the textarea's own, so caret and text can never desynchronize.

export type IPCodeInputTokenKind = 'plain' | 'accent' | 'error' | 'muted';

export interface IPCodeInputToken {
  text: string;
  kind: IPCodeInputTokenKind;
}

export interface IPCodeInputCaretPoint {
  x: number;
  y: number;
}

export interface IPCodeInputHandle {
  focus: () => void;
  getSelectionStart: () => number | null;
  setSelectionRange: (start: number, end: number) => void;
}

interface IPCodeInputProps {
  id: string;
  value: string;
  onChange?: (ev: FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => void;
  getTokens?: (text: string) => IPCodeInputToken[];
  onCaretPointChange?: (point: IPCodeInputCaretPoint | undefined) => void;
  componentRef?: Ref<IPCodeInputHandle>;
  rows?: number;
  maxLength?: number;
  disabled?: boolean;
  errorMessage?: string;
  placeholder?: string;
  role?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
  'aria-autocomplete'?: 'none' | 'inline' | 'list' | 'both';
  'aria-expanded'?: boolean;
  'aria-controls'?: string;
  'aria-activedescendant'?: string;
  onKeyDown?: KeyboardEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onKeyUp?: KeyboardEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onClick?: MouseEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onBlur?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onFocus?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
}

const LINE_HEIGHT_PX = 20;

export const IPCodeInput = (props: IPCodeInputProps) => {
  const theme = useTheme();

  // Both layers share the THEME font, not a custom stack: form controls and regular spans can
  // resolve the same requested family to different actual fonts (local-font policies), which
  // desynchronizes the mirror's box positions from the textarea's glyphs. The theme font is what
  // every rendered TextField already uses, so both layers resolve identically.
  // tabSize is a string on purpose: merge-styles suffixes bare numbers with px, and "tab-size:
  // 4px" means four pixels per tab instead of four characters.
  const metrics = {
    boxSizing: 'border-box',
    fontFamily: theme.fonts.medium.fontFamily,
    fontSize: theme.fonts.medium.fontSize,
    letterSpacing: 'normal',
    lineHeight: `${LINE_HEIGHT_PX}px`,
    overflowWrap: 'break-word',
    padding: '6px 8px',
    tabSize: '4',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  } as const;
  const fieldRef = useRef<ITextField | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const backdropRef = useRef<HTMLSpanElement | null>(null);
  const markerRef = useRef<HTMLSpanElement | null>(null);
  const lastCaretPoint = useRef<IPCodeInputCaretPoint | undefined>(undefined);
  const [caret, setCaret] = useState<number | undefined>(undefined);

  const { componentRef, getTokens, onCaretPointChange, value } = props;

  useImperativeHandle(
    componentRef,
    () => ({
      focus: () => fieldRef.current?.focus(),
      getSelectionStart: () => fieldRef.current?.selectionStart ?? null,
      setSelectionRange: (start: number, end: number) => {
        fieldRef.current?.setSelectionRange(start, end);
        setCaret(start);
      },
    }),
    [],
  );

  const tokens = useMemo(() => (getTokens ? getTokens(value) : undefined), [getTokens, value]);
  const tokensMatch = tokens ? tokens.map((token) => token.text).join('') === value : false;
  const endsWithNewline = value.endsWith('\n');

  // Token kinds paint BACKGROUNDS only; the glyphs themselves come from the textarea. Browsers
  // quantize glyph advances differently inside form controls than in regular spans (visible under
  // display scaling or zoom), so transparent-text-over-colored-glyphs can never align exactly.
  // Backgrounds tolerate that drift; glyphs do not.
  const tokenStyles: Record<IPCodeInputTokenKind, CSSProperties> = {
    plain: {},
    accent: { backgroundColor: theme.palette.themeLighter, borderRadius: 2 },
    error: { backgroundColor: theme.semanticColors.errorBackground, borderRadius: 2 },
    muted: {},
  };

  const renderBackdrop = () => {
    const runs = tokens && tokensMatch ? tokens : [{ text: value, kind: 'plain' as IPCodeInputTokenKind }];
    const output: JSX.Element[] = [];
    let offset = 0;
    let markerPlaced = caret === undefined;

    runs.forEach((run, index) => {
      const end = offset + run.text.length;
      if (!markerPlaced && caret !== undefined && caret >= offset && caret <= end) {
        const split = caret - offset;
        const head = run.text.slice(0, split);
        const tail = run.text.slice(split);
        if (head) output.push(<span key={`${index}-head`} style={tokenStyles[run.kind]}>{head}</span>);
        output.push(<span key="caret" ref={markerRef} />);
        if (tail) output.push(<span key={`${index}-tail`} style={tokenStyles[run.kind]}>{tail}</span>);
        markerPlaced = true;
      } else {
        output.push(<span key={index} style={tokenStyles[run.kind]}>{run.text}</span>);
      }
      offset = end;
    });

    if (!markerPlaced) output.push(<span key="caret" ref={markerRef} />);

    return output;
  };

  const getTextArea = (): HTMLTextAreaElement | null => rootRef.current?.querySelector('textarea') ?? null;

  const reportCaretPoint = () => {
    if (!onCaretPointChange) return;
    const marker = markerRef.current;
    const next = marker
      ? { x: marker.getBoundingClientRect().left, y: marker.getBoundingClientRect().top + LINE_HEIGHT_PX }
      : undefined;
    const previous = lastCaretPoint.current;
    const changed =
      (next === undefined) !== (previous === undefined) ||
      (next !== undefined && previous !== undefined && (next.x !== previous.x || next.y !== previous.y));

    if (changed) {
      lastCaretPoint.current = next;
      onCaretPointChange(next);
    }
  };

  const syncMirror = () => {
    const textArea = getTextArea();
    const backdrop = backdropRef.current;
    if (textArea && backdrop) {
      backdrop.style.width = `${textArea.clientWidth}px`;
      backdrop.style.height = `${textArea.clientHeight}px`;
      backdrop.scrollLeft = textArea.scrollLeft;
      backdrop.scrollTop = textArea.scrollTop;
    }
    reportCaretPoint();
  };

  const updateCaret = (element?: HTMLInputElement | HTMLTextAreaElement) => {
    const next = element?.selectionStart ?? fieldRef.current?.selectionStart ?? undefined;
    setCaret(next === null ? undefined : next);
  };

  useLayoutEffect(syncMirror);

  useLayoutEffect(() => {
    const textArea = getTextArea();
    if (!textArea || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(syncMirror);
    observer.observe(textArea);

    return () => observer.disconnect();
    // The observed element is stable for the lifetime of this controlled field.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The mirror's text is invisible; it exists so the token backgrounds occupy exactly the space
  // the (visible) textarea glyphs occupy, including wrapping.
  const backdropStyle: CSSProperties = {
    ...metrics,
    color: 'transparent',
    display: 'block',
    margin: 0,
    overflow: 'hidden',
    pointerEvents: 'none',
  };

  return (
    <IPTextField
      id={props.id}
      componentRef={fieldRef}
      elementRef={(element) => {
        rootRef.current = element;
      }}
      multiline
      resizable
      value={value}
      rows={props.rows ?? 3}
      maxLength={props.maxLength}
      disabled={props.disabled}
      errorMessage={props.errorMessage}
      placeholder={props.placeholder}
      spellCheck={false}
      role={props.role}
      aria-labelledby={props['aria-labelledby']}
      aria-describedby={props['aria-describedby']}
      aria-autocomplete={props['aria-autocomplete']}
      aria-expanded={props['aria-expanded']}
      aria-controls={props['aria-controls']}
      aria-activedescendant={props['aria-activedescendant']}
      onRenderPrefix={() => (
        <span ref={backdropRef} style={backdropStyle} aria-hidden={true}>
          {renderBackdrop()}
          {endsWithNewline && <br />}
        </span>
      )}
      styles={{
        fieldGroup: { position: 'relative' },
        prefix: {
          // Fluent gives the prefix slot an opaque neutralLighter background; the mirror must not
          // repaint the input surface.
          background: 'transparent',
          bottom: 0,
          display: 'block',
          left: 0,
          overflow: 'hidden',
          padding: 0,
          pointerEvents: 'none',
          position: 'absolute',
          right: 0,
          top: 0,
        },
        field: {
          ...metrics,
          background: 'transparent',
          color: props.disabled ? theme.semanticColors.disabledText : theme.semanticColors.inputText,
          maxHeight: 200,
          minHeight: 72,
          overflow: 'auto',
          // The absolutely positioned prefix mirror paints above in-flow content; the textarea
          // must sit above it so the caret and selection stay visible.
          position: 'relative',
          zIndex: 1,
          selectors: {
            '::placeholder': {
              color: theme.semanticColors.inputPlaceholderText,
              opacity: 1,
            },
          },
        },
      }}
      onChange={(event: FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
        updateCaret(event.currentTarget);
        props.onChange?.(event, newValue);
      }}
      onScroll={(event: FormEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        updateCaret(event.currentTarget);
        syncMirror();
      }}
      onSelect={(event: FormEvent<HTMLInputElement | HTMLTextAreaElement>) => updateCaret(event.currentTarget)}
      onKeyDown={props.onKeyDown}
      onKeyUp={(event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        updateCaret(event.currentTarget);
        props.onKeyUp?.(event);
      }}
      onClick={(event: MouseEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        updateCaret(event.currentTarget);
        props.onClick?.(event);
      }}
      onFocus={(event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        updateCaret(event.currentTarget);
        props.onFocus?.(event);
      }}
      onBlur={props.onBlur}
    />
  );
};
